import React, { createContext, useContext, useEffect, useRef, useState } from 'react'
import { useAuth } from '../auth'

export type FontTier = 'standard' | 'large' | 'xlarge'
export type ThemeMode = 'light' | 'green'
export interface Prefs { fontTier: FontTier; theme: ThemeMode }

const fontKey = (uid: string) => `cswd_font:${uid}`
const themeKey = (uid: string) => `cswd_theme:${uid}`

function loadPrefs(uid: string | null): Prefs {
  if (!uid) return { fontTier: 'standard', theme: 'light' }
  const fontTier = (localStorage.getItem(fontKey(uid)) as FontTier | null) ?? 'standard'
  const rawTheme = localStorage.getItem(themeKey(uid))
  // Migrate legacy 'dark' value to 'green'
  const theme: ThemeMode = rawTheme === 'dark' ? 'green' : ((rawTheme as ThemeMode | null) ?? 'light')
  return { fontTier, theme }
}

function applyToDOM(prefs: Prefs): void {
  document.documentElement.setAttribute('data-theme', prefs.theme)
  document.documentElement.setAttribute('data-font', prefs.fontTier)
}

interface PrefsContextValue {
  fontTier: FontTier
  theme: ThemeMode
  setFontTier: (t: FontTier) => void
  setTheme: (t: ThemeMode) => void
}

const PrefsContext = createContext<PrefsContextValue | null>(null)

export function PrefsProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth()
  const uid = user?.id ?? null
  const prevUidRef = useRef<string | null>(undefined as unknown as string | null)

  const [prefs, setPrefs] = useState<Prefs>(() => {
    const p = loadPrefs(uid)
    applyToDOM(p)
    return p
  })

  useEffect(() => {
    if (prevUidRef.current !== uid) {
      prevUidRef.current = uid
      const p = loadPrefs(uid)
      setPrefs(p)
      applyToDOM(p)
    }
  }, [uid])

  const setFontTier = (fontTier: FontTier) => {
    const next = { ...prefs, fontTier }
    setPrefs(next)
    applyToDOM(next)
    if (uid) localStorage.setItem(fontKey(uid), fontTier)
  }

  const setTheme = (theme: ThemeMode) => {
    const next = { ...prefs, theme }
    setPrefs(next)
    applyToDOM(next)
    if (uid) localStorage.setItem(themeKey(uid), theme)
  }

  return (
    <PrefsContext.Provider value={{ ...prefs, setFontTier, setTheme }}>
      {children}
    </PrefsContext.Provider>
  )
}

export function usePrefs(): PrefsContextValue {
  const ctx = useContext(PrefsContext)
  if (ctx === null) throw new Error('usePrefs must be used within a PrefsProvider')
  return ctx
}
